import { useState, useCallback, useRef, useMemo } from 'react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    ReactFlow,
    ReactFlowProvider,
    addEdge,
    useNodesState,
    useEdgesState,
    useReactFlow,
    Controls,
    Background,
    BackgroundVariant
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
    WORKFLOW_CANVAS_CLASS,
    WORKFLOW_CANVAS_DOT_COLOR,
    WORKFLOW_CANVAS_DOT_SIZE,
    WORKFLOW_EDGE_OPTIONS,
    workflowTheme
} from './utils/workflowBuilderTheme';
import { BuilderSkeleton } from './components/skeletons/WhatsAppPageSkeletons';
import { useColorModeValue } from '../../components/ui/color-mode';

import { ArrowLeft, Save, Rocket, CheckCircle2, AlertCircle, Sparkles, Loader2, Database, Bot, GitBranch, CirclePlay, Settings, Undo2, Redo2 } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ActionNode, AINode, ConditionNode, MessageNode, TriggerNode } from './components/WorkFlow/CustomNodes';
import { WorkflowStartNode } from './components/WorkFlow/WorkflowStartNode';
import WorkflowDeletableEdge from './components/WorkFlow/WorkflowDeletableEdge';
import { Button } from '../../components/tailwind/button';
import ConfirmDialog from '../../components/tailwind/ConfirmDialog';
import { LeftPalette, PaletteDrawerHandle } from './components/WorkFlow/LeftPallete';
import { RightInspectorCatalog } from './components/WorkFlow/RightInspectorCatalog';
import { WorkflowPlayDemoDialog } from './components/WorkFlow/WorkflowPlayDemoDialog';
import { KnowledgeBaseTab } from './components/WorkFlow/KnowledgeBaseTab';
import { AIOrchestratorTab } from './components/WorkFlow/AIOrchestratorTab';
import { WorkflowSettingsPanel } from './components/WorkFlow/WorkflowSettingsPanel';
import { WorkflowUnsavedChangesDialog } from './components/WorkFlow/WorkflowDraftDialogs';
import { useWorkflowUnsavedGuard } from './hooks/useWorkflowUnsavedGuard';
import { useWorkflowMediaFlush } from './hooks/useWorkflowMediaFlush';
import { usePageTitle } from '../../hooks/usePageTitle';
import { createWorkflowCanvasSnapshot, useWorkflowCanvasHistory } from './hooks/useWorkflowCanvasHistory';
import { WorkflowCanvasViewport } from './hooks/useWorkflowCanvasViewport';
import { showAlert } from '../../store/alertSlice';
import {
    clearWorkflowDetail,
    fetchWorkflowCatalogRequest,
    fetchWorkflowDetailRequest,
    fetchWorkflowListRequest,
    publishWorkflowRequest,
    clearWorkflowPublishState,
    clearWorkflowSaveState
} from '../../store/slices/workflow';
import { mapWorkflowDefinitionToFlow, isWorkflowEndNode, DEFAULT_SESSION_TIMEOUT_SECONDS } from './utils/workflowFlowMapper';
import { resolveNewWorkflowCanvas } from './utils/resolveNewWorkflowCanvas';
import { getCanvasNodePreviewText, PALETTE } from './utils/workflowPaletteConstants';
import { getWorkflowNodeDescription, normalizeNodeConfigForPalette, applyAutoSaveToContext, validateNodeKeyValues, validateNodeMessageContent, validateSessionTimeout } from './utils/workflowInspectorUx';
import { resolveNodeConfigSchema } from './utils/workflowSchemaUtils';
import { buildWorkflowDemoScript } from './utils/buildWorkflowDemoScript';
import {
    WORKFLOW_START_NODE_ID,
    WORKFLOW_START_NODE_TYPE,
    createWorkflowStartCanvasEdge,
    getWorkflowNodes,
    getWorkflowStartNode,
    isWorkflowStartCanvasEdge,
    isWorkflowStartCanvasNode,
    resolveWorkflowStartNodePosition,
    setWorkflowStartNodePosition,
    withWorkflowStartNode,
} from './utils/workflowStartNode';

const nodeTypes = {
    trigger: TriggerNode,
    message: MessageNode,
    condition: ConditionNode,
    action: ActionNode,
    ai: AINode,
    [WORKFLOW_START_NODE_TYPE]: WorkflowStartNode,
};

const edgeTypes = {
    workflowDeletable: WorkflowDeletableEdge,
};

const NEW_WORKFLOW_IDS = new Set(["new", "new-flow-ai"]);
const isNewWorkflowRoute = (routeId) => NEW_WORKFLOW_IDS.has(routeId);

/** Half of default canvas node size — centers palette drops under the cursor. */
const CANVAS_NODE_DROP_OFFSET = { x: 110, y: 48 };

const readSavedWorkflowId = (saveResponse) =>
    saveResponse?.workflowId
    || saveResponse?.workflow?.workflowId
    || null;

const readSavedDraftVersion = (saveResponse, workflow) =>
    saveResponse?.draftVersion
    ?? saveResponse?.workflow?.draftVersion
    ?? saveResponse?.version
    ?? workflow?.draftVersion
    ?? workflow?.version
    ?? 1;

const formatIntentName = (intent = "") =>
    intent
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");

function WorkFlowBuilder({ id }) {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    usePageTitle(id === "new" ? "New Workflow" : "Workflow Builder");
    const {
        workflow: workflowDetail,
        loading: workflowDetailLoading,
        error: workflowDetailError
    } = useSelector((state) => state.workflowDetail || {});
    const workflowSave = useSelector((state) => state.workflowSave || {});
    const workflowCatalogData = useSelector((state) => state.workflowCatalog?.data);
    const workflowPublish = useSelector((state) => state.workflowPublish || {});

    const isMediaUploading = workflowSave?.mediaUpload?.loading ?? false;
    const isSaving = workflowSave?.loading ?? false;
    const isPublishing = workflowPublish?.loading ?? false;
    const isAnyActionPending = isMediaUploading || isSaving || isPublishing;

    const initialCanvas = useMemo(
        () => resolveNewWorkflowCanvas(id, location.state ?? {}),
        [id, location.state]
    );

    const [nodes, setNodes, onNodesChange] = useNodesState(() =>
        withWorkflowStartNode(
            initialCanvas.nodes,
            resolveWorkflowStartNodePosition(initialCanvas.nodes, initialCanvas.start || "")
        )
    );
    const [edges, setEdges, onEdgesChange] = useEdgesState(initialCanvas.edges);
    const [name, setName] = useState(initialCanvas.name);
    const [selectedNode, setSelectedNode] = useState(null);
    const [selectedEdgeId, setSelectedEdgeId] = useState(null);
    const [inspectorOpen, setInspectorOpen] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [workflowStart, setWorkflowStart] = useState(initialCanvas.start || "");
    const [sessionTimeoutSeconds, setSessionTimeoutSeconds] = useState(initialCanvas.sessionTimeoutSeconds ?? DEFAULT_SESSION_TIMEOUT_SECONDS);
    const [globalTransitions, setGlobalTransitions] = useState(initialCanvas.globalTransitions ?? {});
    const [activeTab, setActiveTab] = useState("builder");
    const [paletteOpen, setPaletteOpen] = useState(true);
    const canvasDotColor = useColorModeValue(WORKFLOW_CANVAS_DOT_COLOR.light, WORKFLOW_CANVAS_DOT_COLOR.dark);
    const [aiRefinementQuery, setAiRefinementQuery] = useState("");
    const [isRefining, setIsRefining] = useState(false);
    const [replaceWorkflowConfirmOpen, setReplaceWorkflowConfirmOpen] = useState(false);
    const [demoOpen, setDemoOpen] = useState(false);
    const [intentName, setIntentName] = useState("");
    const [exampleUtterances, setExampleUtterances] = useState([""]);
    const [savedWorkflowId, setSavedWorkflowId] = useState(null);
    const [hydrationReady, setHydrationReady] = useState(false);
    const pendingSaveWorkflowIdRef = useRef(null);
    const inspectorPendingMediaRef = useRef(false);
    const nodesRef = useRef(initialCanvas.nodes);
    const edgesRef = useRef(initialCanvas.edges);
    const workflowStartRef = useRef(workflowStart);

    const {
        canUndo,
        canRedo,
        resetHistory,
        recordBeforeChange,
        recordBeforeInspectorChange,
        undo: undoCanvasHistory,
        redo: redoCanvasHistory,
    } = useWorkflowCanvasHistory();

    const getCanvasSnapshot = useCallback(() => createWorkflowCanvasSnapshot({
        nodes: nodesRef.current,
        edges: edgesRef.current,
        workflowStart: workflowStartRef.current,
    }), []);

    const applyCanvasSnapshot = useCallback((snapshot) => {
        setNodes((current) => {
            const start = getWorkflowStartNode(current);
            if (start) {
                return [start, ...snapshot.nodes];
            }
            return withWorkflowStartNode(
                snapshot.nodes,
                resolveWorkflowStartNodePosition(snapshot.nodes, snapshot.workflowStart)
            );
        });
        setEdges(snapshot.edges);
        setWorkflowStart(snapshot.workflowStart);
        nodesRef.current = snapshot.nodes;
        edgesRef.current = snapshot.edges;
        workflowStartRef.current = snapshot.workflowStart;
        setSelectedNode((current) => {
            if (!current) return null;
            return snapshot.nodes.find((node) => node.id === current.id) || null;
        });
        setValidationErrors([]);
        setSelectedEdgeId(null);
    }, [setNodes, setEdges]);

    const handleUndo = useCallback(() => {
        undoCanvasHistory(getCanvasSnapshot(), applyCanvasSnapshot);
    }, [undoCanvasHistory, getCanvasSnapshot, applyCanvasSnapshot]);

    const handleRedo = useCallback(() => {
        redoCanvasHistory(getCanvasSnapshot(), applyCanvasSnapshot);
    }, [redoCanvasHistory, getCanvasSnapshot, applyCanvasSnapshot]);

    useEffect(() => {
        const onKeyDown = (event) => {
            const target = event.target;
            const tagName = target?.tagName;
            if (
                tagName === "INPUT"
                || tagName === "TEXTAREA"
                || tagName === "SELECT"
                || target?.isContentEditable
            ) {
                return;
            }

            const mod = event.ctrlKey || event.metaKey;
            if (!mod) return;

            if (event.key === "z" && !event.shiftKey) {
                event.preventDefault();
                handleUndo();
                return;
            }

            if (event.key === "y" || (event.key === "z" && event.shiftKey)) {
                event.preventDefault();
                handleRedo();
            }
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [handleUndo, handleRedo]);

    useEffect(() => {
        nodesRef.current = getWorkflowNodes(nodes);
    }, [nodes]);

    useEffect(() => {
        edgesRef.current = edges;
    }, [edges]);

    useEffect(() => {
        workflowStartRef.current = workflowStart;
    }, [workflowStart]);

    const updateNodeData = useCallback((nodeId, updater) => {
        recordBeforeInspectorChange(getCanvasSnapshot());
        setNodes((nds) => {
            const next = nds.map((node) => {
                if (node.id !== nodeId) return node;
                const prevData = node.data || {};
                const nextData = typeof updater === "function" ? updater(prevData) : updater;
                const updated = { ...node, data: nextData };
                setSelectedNode((sel) => (sel?.id === nodeId ? updated : sel));
                return updated;
            });
            nodesRef.current = getWorkflowNodes(next);
            return next;
        });
    }, [setNodes, recordBeforeInspectorChange, getCanvasSnapshot]);

    const { mediaFlushSignal, uploadSilent, flushPendingMedia, registerPendingUploadNode } = useWorkflowMediaFlush({
        nodesRef,
        updateNodeData,
        workflowSave,
        inspectorPendingMediaRef,
    });

    const notifyBuilder = useCallback((message, type = "error") => {
        dispatch(showAlert({ message, type }));
    }, [dispatch]);

    const workflowNodes = useMemo(() => getWorkflowNodes(nodes), [nodes]);

    const {
        saveNow,
        confirmLeave,
        leaveDialogProps,
    } = useWorkflowUnsavedGuard({
        routeId: id,
        nodes: workflowNodes,
        edges,
        name,
        workflowStart,
        sessionTimeoutSeconds,
        globalTransitions,
        intentName,
        exampleUtterances,
        formatIntentName,
        pendingSaveWorkflowIdRef,
        workflowSave,
        dispatch,
        hydrationReady,
        onNotify: notifyBuilder,
        flushPendingMedia,
        nodesRef,
        edgesRef,
        workflowStartRef,
    });

    const resolveWorkflowId = useCallback(() => {
        if (!isNewWorkflowRoute(id)) return id;
        return savedWorkflowId
            || readSavedWorkflowId(workflowSave.response)
            || pendingSaveWorkflowIdRef.current;
    }, [id, savedWorkflowId, workflowSave.response]);

    const demoScript = useMemo(
        () => buildWorkflowDemoScript({
            nodes: workflowNodes,
            edges,
            start: workflowStart,
            workflowName: name
        }),
        [workflowNodes, edges, workflowStart, name]
    );

    const handleDeleteEdge = useCallback((edge) => {
        recordBeforeChange(getCanvasSnapshot());
        setSelectedEdgeId(null);
        if (isWorkflowStartCanvasEdge(edge)) {
            workflowStartRef.current = "";
            setWorkflowStart("");
            return;
        }
        setEdges((currentEdges) => {
            const nextEdges = currentEdges.filter((item) => item.id !== edge.id);
            edgesRef.current = nextEdges;
            return nextEdges;
        });
    }, [recordBeforeChange, getCanvasSnapshot, setEdges]);

    const decorateCanvasEdge = useCallback((edge) => ({
        ...edge,
        type: "workflowDeletable",
        selected: edge.id === selectedEdgeId,
        data: {
            ...edge.data,
            onDelete: () => handleDeleteEdge(edge),
        },
    }), [selectedEdgeId, handleDeleteEdge]);

    const canvasEdges = useMemo(() => {
        const decoratedEdges = edges.map(decorateCanvasEdge);
        if (!workflowStart) return decoratedEdges;
        const startTarget = workflowNodes.find((node) => node.id === workflowStart);
        if (!startTarget || isWorkflowEndNode(startTarget)) return decoratedEdges;
        return [
            ...decoratedEdges,
            decorateCanvasEdge(createWorkflowStartCanvasEdge(workflowStart)),
        ];
    }, [edges, workflowNodes, workflowStart, decorateCanvasEdge]);

    const handlePlayDemo = () => {
        if (demoScript.isEmpty) {
            dispatch(showAlert({
                message: "Add message or input steps to preview this workflow.",
                type: "error"
            }));
            return;
        }
        setDemoOpen(true);
    };

    const workflowStatus = isNewWorkflowRoute(id)
        ? "DRAFT"
        : (workflowDetail?.status || 
           workflowDetail?.versions?.find(v => v.version === (workflowDetail?.draftVersion || workflowDetail?.version || 1))?.status || 
           "DRAFT");
    const isLive = workflowStatus === "ACTIVE";

    const reactFlowWrapper = useRef(null);
    const { screenToFlowPosition } = useReactFlow();

    const workflowList = useSelector((state) => state.workflowList?.workflows || []);

    useEffect(() => {
        dispatch(fetchWorkflowCatalogRequest({
            operation: "GET_CATALOG",
            payload: { catalogType: "all", channel: ["whatsapp"] }
        }));
        dispatch(fetchWorkflowListRequest({ operation: "LIST_WORKFLOWS", payload: {} }));
    }, [dispatch]);

    useEffect(() => {
        if (!workflowList.length) return;

        setNodes((nds) => {
            const start = getWorkflowStartNode(nds);
            const updated = getWorkflowNodes(nds).map((node) => {
                if (
                    node.data.paletteType !== PALETTE.TRANSFER_WORKFLOW
                    && node.data.paletteType !== PALETTE.INVOKE_WORKFLOW
                ) {
                    return node;
                }
                const targetId = node.data.config?.workflowId;
                if (!targetId) return node;
                const target = workflowList.find((wf) => wf.workflowId === targetId);
                if (!target?.name) return node;
                const description = getWorkflowNodeDescription(node.data.paletteType, target.name);
                if (node.data.description === description) return node;
                return { ...node, data: { ...node.data, description } };
            });
            return start ? [start, ...updated] : withWorkflowStartNode(updated);
        });
    }, [workflowList, setNodes]);

    useEffect(() => {
        if (!id) return;

        const resetCanvas = (canvasState) => {
            setName(canvasState.name);
            setNodes(withWorkflowStartNode(
                canvasState.nodes,
                resolveWorkflowStartNodePosition(canvasState.nodes, canvasState.start || "")
            ));
            setEdges(canvasState.edges);
            setWorkflowStart(canvasState.start || "");
            setSessionTimeoutSeconds(canvasState.sessionTimeoutSeconds ?? DEFAULT_SESSION_TIMEOUT_SECONDS);
            setGlobalTransitions(canvasState.globalTransitions ?? {});
            setSelectedNode(null);
            setSelectedEdgeId(null);
            setInspectorOpen(false);
            setValidationErrors([]);
            resetHistory();
        };

        if (isNewWorkflowRoute(id)) {
            dispatch(clearWorkflowDetail());
            if (location.state?.cloneOf) {
                setHydrationReady(false);
                resetCanvas({
                    name: location.state.workflowName || "Untitled Workflow",
                    nodes: [],
                    edges: [],
                    start: "",
                    sessionTimeoutSeconds: DEFAULT_SESSION_TIMEOUT_SECONDS,
                    globalTransitions: {}
                });
                const version = location.state.version;
                dispatch(fetchWorkflowDetailRequest({
                    operation: "GET_WORKFLOW",
                    payload: {
                        workflowId: location.state.cloneOf,
                        ...(version != null ? { version } : {})
                    }
                }));
            } else {
                resetCanvas(resolveNewWorkflowCanvas(id, location.state ?? {}));
                setHydrationReady(true);
            }
            return;
        }

        setHydrationReady(false);

        const isPostSaveRoute =
            location.state?.fromSave
            || pendingSaveWorkflowIdRef.current === id
            || savedWorkflowId === id;

        dispatch(clearWorkflowDetail());
        if (!isPostSaveRoute) {
            resetCanvas({
                name: "Untitled Workflow",
                nodes: [],
                edges: [],
                start: "",
                sessionTimeoutSeconds: DEFAULT_SESSION_TIMEOUT_SECONDS,
                globalTransitions: {}
            });
        }

        const version = location.state?.version;
        dispatch(fetchWorkflowDetailRequest({
            operation: "GET_WORKFLOW",
            payload: {
                workflowId: id,
                ...(version != null ? { version } : {})
            }
        }));
    }, [dispatch, id, savedWorkflowId, location.state, setEdges, setNodes, resetHistory]);

    useEffect(() => {
        if (!workflowDetail?.definition) return;

        const isCloning = isNewWorkflowRoute(id) && location.state?.cloneOf;
        if (!isCloning) {
            if (!id || isNewWorkflowRoute(id)) return;
            if (workflowDetail.workflowId !== id) return;
        } else {
            if (workflowDetail.workflowId !== location.state.cloneOf) return;
        }

        const mapped = mapWorkflowDefinitionToFlow(workflowDetail.definition);
        if (isCloning) {
            setName(location.state?.workflowName || `${workflowDetail?.name || "Untitled Workflow"} Clone`);
        } else {
            setName(workflowDetail?.name || "Untitled Workflow");
        }
        setNodes(withWorkflowStartNode(
            mapped.nodes,
            resolveWorkflowStartNodePosition(mapped.nodes, mapped.start || "")
        ));
        setEdges(mapped.edges);
        setWorkflowStart(mapped.start || "");
        setSessionTimeoutSeconds(mapped.sessionTimeoutSeconds ?? DEFAULT_SESSION_TIMEOUT_SECONDS);
        setGlobalTransitions(mapped.globalTransitions ?? {});
        setSelectedEdgeId(null);
        if (isCloning) {
            setIntentName("");
            setExampleUtterances([""]);
        } else {
            setIntentName(workflowDetail?.definition?.intent || "");
            const existingUtterances = workflowDetail?.definition?.routing?.examples || [];
            setExampleUtterances(existingUtterances.length > 0 ? existingUtterances : [""]);
        }
        resetHistory();
    }, [workflowDetail, id, location.state, setEdges, setNodes, resetHistory]);

    useEffect(() => {
        if (!workflowCatalogData) return;

        setNodes((nds) => {
            const start = getWorkflowStartNode(nds);
            const updated = getWorkflowNodes(nds).map((node) => {
                const schema = resolveNodeConfigSchema(
                    workflowCatalogData,
                    node.data.workflowType,
                    node.data.paletteType
                );
                if (!schema || !Object.keys(schema).length) return node;
                return { ...node, data: { ...node.data, paletteConfigSchema: schema } };
            });
            return start ? [start, ...updated] : withWorkflowStartNode(updated);
        });
    }, [workflowCatalogData, setNodes]);

    useEffect(() => {
        if (!hydrationReady || !workflowNodes.length || !workflowStart) return;

        const startNode = workflowNodes.find((node) => node.id === workflowStart);
        if (startNode && !isWorkflowEndNode(startNode)) return;

        setWorkflowStart("");
        workflowStartRef.current = "";
    }, [workflowNodes, workflowStart, hydrationReady]);

    useEffect(() => {
        const isCloning = isNewWorkflowRoute(id) && location.state?.cloneOf;
        if (!isCloning) {
            if (isNewWorkflowRoute(id)) return;
            if (!workflowCatalogData) return;
            if (!workflowDetail?.definition || workflowDetail.workflowId !== id) return;
        } else {
            if (!workflowCatalogData) return;
            if (!workflowDetail?.definition || workflowDetail.workflowId !== location.state.cloneOf) return;
        }

        setHydrationReady(true);
    }, [id, workflowCatalogData, workflowDetail, location.state]);

    useEffect(() => () => {
        dispatch(clearWorkflowDetail());
        dispatch(clearWorkflowPublishState());
        dispatch(clearWorkflowSaveState());
    }, [dispatch]);

    useEffect(() => {
        if (!workflowPublish?.response?.workflowId) return;
        dispatch(clearWorkflowPublishState());
        navigate("/whatsapp/workflows");
    }, [dispatch, navigate, workflowPublish?.response?.workflowId]);

    useEffect(() => {
        if (workflowSave.loading || !workflowSave.response) return;

        // Guard against stale save responses from previous mounts/sessions
        if (!pendingSaveWorkflowIdRef.current) return;

        const persistedId = readSavedWorkflowId(workflowSave.response) || pendingSaveWorkflowIdRef.current;
        if (!persistedId) return;

        setSavedWorkflowId(persistedId);

        const draftVersion = readSavedDraftVersion(workflowSave.response, workflowDetail);

        // Prevent infinite navigation loops if location.state is already updated
        if (location.state?.fromSave && location.state?.version === draftVersion) {
            return;
        }

        if (isNewWorkflowRoute(id) && id !== persistedId) {
            navigate(`/whatsapp/workflows/${persistedId}/edit`, {
                replace: true,
                state: { fromSave: true, version: draftVersion }
            });
        } else {
            navigate(`/whatsapp/workflows/${id}/edit`, {
                replace: true,
                state: {
                    ...location.state,
                    fromSave: true,
                    version: draftVersion
                }
            });
        }
    }, [workflowSave.loading, workflowSave.response, id, navigate, location.state, workflowDetail]);

    const onConnect = useCallback(
        (params) => {
            if (params.source === WORKFLOW_START_NODE_ID) {
                const targetNode = nodesRef.current.find((node) => node.id === params.target);
                if (!targetNode || isWorkflowEndNode(targetNode)) return;

                recordBeforeChange(getCanvasSnapshot());
                workflowStartRef.current = params.target;
                setWorkflowStart(params.target);
                setNodes((nds) => setWorkflowStartNodePosition(
                    nds,
                    resolveWorkflowStartNodePosition(nodesRef.current, params.target)
                ));
                return;
            }

            recordBeforeChange(getCanvasSnapshot());
            setEdges((eds) => addEdge({ ...params, ...WORKFLOW_EDGE_OPTIONS }, eds));
        },
        [setEdges, recordBeforeChange, getCanvasSnapshot, setNodes],
    );

    const handleNodesChange = useCallback((changes) => {
        const filtered = changes.filter((change) => {
            if (isWorkflowStartCanvasNode({ id: change.id }) && change.type === "remove") {
                return false;
            }
            return true;
        });
        if (!filtered.length) return;

        if (filtered.some((change) => change.type === "remove")) {
            recordBeforeChange(getCanvasSnapshot());
        }
        onNodesChange(filtered);
    }, [onNodesChange, recordBeforeChange, getCanvasSnapshot]);

    const handleEdgesChange = useCallback((changes) => {
        const startEdgeRemovals = changes.filter(
            (change) => change.type === "remove" && isWorkflowStartCanvasEdge({ id: change.id })
        );
        const realChanges = changes.filter(
            (change) => !(change.type === "remove" && isWorkflowStartCanvasEdge({ id: change.id }))
        );

        const shouldRecord = startEdgeRemovals.length > 0
            || realChanges.some((change) => change.type === "remove");

        if (shouldRecord) {
            recordBeforeChange(getCanvasSnapshot());
        }

        if (startEdgeRemovals.length) {
            workflowStartRef.current = "";
            setWorkflowStart("");
        }

        if (realChanges.length) {
            onEdgesChange(realChanges);
        }
    }, [onEdgesChange, recordBeforeChange, getCanvasSnapshot]);

    const onNodeDragStart = useCallback(() => {
        recordBeforeChange(getCanvasSnapshot());
    }, [recordBeforeChange, getCanvasSnapshot]);

    const onDragOver = useCallback((event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    const onDrop = useCallback(
        (event) => {
            event.preventDefault();
            const type = event.dataTransfer.getData('application/reactflow/type');
            const label = event.dataTransfer.getData('application/reactflow/label');
            const catalogItemRaw = event.dataTransfer.getData("application/reactflow/catalog-item");

            if (typeof type === 'undefined' || !type) return;

            const position = screenToFlowPosition({
                x: event.clientX,
                y: event.clientY,
            });
            position.x -= CANVAS_NODE_DROP_OFFSET.x;
            position.y -= CANVAS_NODE_DROP_OFFSET.y;

            const catalogItem = JSON.parse(catalogItemRaw);

            const nodeConfig = normalizeNodeConfigForPalette(
                structuredClone(catalogItem.defaultConfig ?? {}),
                catalogItem.paletteType || catalogItem.id,
                label
            );
            let nodeData = applyAutoSaveToContext({
                label,
                description: "",
                hasError: false,
                paletteType: catalogItem.paletteType || catalogItem.id,
                workflowType: catalogItem.workflowType,
                paletteConfigSchema: catalogItem.paletteConfigSchema,
                config: nodeConfig,
                logic: "AND",
                conditions: [],
                saveToContext: undefined
            });
            const preview = getCanvasNodePreviewText(nodeData);
            if (preview) nodeData.description = preview;

            const newNode = {
                id: crypto.randomUUID(),
                type,
                position,
                data: nodeData,
            };

            recordBeforeChange(getCanvasSnapshot());
            setNodes((nds) => nds.concat(newNode));
            nodesRef.current = [...nodesRef.current, newNode];
            setSelectedNode(newNode);
            setInspectorOpen(true);
        },
        [setNodes, recordBeforeChange, getCanvasSnapshot, screenToFlowPosition],
    );

    const onNodeClick = (event, node) => {
        if (isWorkflowStartCanvasNode(node)) return;
        setSelectedEdgeId(null);
        setSelectedNode(node);
        setInspectorOpen(true);
    };

    const onEdgeClick = useCallback((event, edge) => {
        event.stopPropagation();
        setSelectedEdgeId(edge.id);
        setSelectedNode(null);
        setInspectorOpen(false);
    }, []);

    const onPaneClick = () => {
        setSelectedEdgeId(null);
        setSelectedNode(null);
        setInspectorOpen(false);
    };

    const deleteNode = (nodeId) => {
        recordBeforeChange(getCanvasSnapshot());
        const remainingEdges = edges.filter(
            (edge) => edge.source !== nodeId && edge.target !== nodeId
        );

        setNodes((nds) => {
            const remaining = nds.filter((node) => node.id !== nodeId);
            if (workflowStartRef.current === nodeId) {
                workflowStartRef.current = "";
            }
            setWorkflowStart((current) => (current !== nodeId ? current : ""));
            nodesRef.current = getWorkflowNodes(remaining);
            return remaining;
        });
        setEdges(() => {
            edgesRef.current = remainingEdges;
            return remainingEdges;
        });
        if (selectedNode?.id === nodeId) {
            setSelectedNode(null);
            setInspectorOpen(false);
        }
    };

    const handleSave = () => {
        const errors = [];
        const newNodes = [...workflowNodes];

        const timeoutError = validateSessionTimeout(sessionTimeoutSeconds);
        if (timeoutError) {
            errors.push(timeoutError);
        }

        newNodes.forEach((n, i) => {
            const kvErrors = validateNodeKeyValues(n.data);
            const messageErrors = validateNodeMessageContent(n.data);
            errors.push(...kvErrors, ...messageErrors);
            newNodes[i] = { ...n, data: { ...n.data, hasError: kvErrors.length > 0 || messageErrors.length > 0 } };
        });

        if (errors.length > 0) {
            setNodes((current) => {
                const start = getWorkflowStartNode(current);
                return start ? [start, ...newNodes] : withWorkflowStartNode(newNodes);
            });
            if (selectedNode) {
                const updatedNode = newNodes.find(n => n.id === selectedNode.id);
                if (updatedNode) setSelectedNode(updatedNode);
            }
            setValidationErrors(errors);
            dispatch(showAlert({
                message: "Please fix the highlighted errors before saving draft.",
                type: "error"
            }));
            return;
        }

        void saveNow({ silent: false });
        setValidationErrors([]);
        setNodes(nds => nds.map(n => ({ ...n, data: { ...n.data, hasError: false } })));
        if (selectedNode) {
            setSelectedNode(prev => prev ? { ...prev, data: { ...prev.data, hasError: false } } : null);
        }
    };

    const handlePublish = () => {
        // Phase 1 Validation rules
        const errors = [];
        const newNodes = [...workflowNodes];

        const timeoutError = validateSessionTimeout(sessionTimeoutSeconds);
        if (timeoutError) {
            errors.push(timeoutError);
        }

        newNodes.forEach((n, i) => {
            let hasError = false;

            const messageErrors = validateNodeMessageContent(n.data);
            if (messageErrors.length > 0) {
                errors.push(...messageErrors);
                hasError = true;
            }

            // Check for disconnected nodes (must have at least one connected edge, unless it's the only node)
            if (newNodes.length > 1) {
                const isConnected = edges.some(e => e.source === n.id || e.target === n.id);
                if (!isConnected) {
                    errors.push(`Step "${n.data.label}" is disconnected from the flow.`);
                    hasError = true;
                }
            }

            const kvErrors = validateNodeKeyValues(n.data);
            if (kvErrors.length > 0) {
                errors.push(...kvErrors);
                hasError = true;
            }

            newNodes[i] = { ...n, data: { ...n.data, hasError } };
        });

        setNodes((current) => {
            const start = getWorkflowStartNode(current);
            return start ? [start, ...newNodes] : withWorkflowStartNode(newNodes);
        });
        if (selectedNode) {
            const updatedNode = newNodes.find(n => n.id === selectedNode.id);
            if (updatedNode) setSelectedNode(updatedNode);
        }
        setValidationErrors(errors);

        if (errors.length === 0) {
            const workflowId = resolveWorkflowId();
            const hasSavedDraft = Boolean(workflowSave.response || savedWorkflowId || pendingSaveWorkflowIdRef.current);

            if (!workflowId || (isNewWorkflowRoute(id) && !hasSavedDraft)) {
                dispatch(showAlert({
                    message: "Please save your workflow as a draft before publishing.",
                    type: "error"
                }));
                return;
            }

            const versionToPublish = readSavedDraftVersion(workflowSave.response, workflowDetail);

            dispatch(publishWorkflowRequest({
                operation: "PUBLISH_WORKFLOW",
                payload: {
                    workflowId,
                    version: versionToPublish
                }
            }));
        } else {
            dispatch(showAlert({
                message: "Please fix the highlighted errors before publishing.",
                type: "error"
            }));
        }
    };

    const executeRefine = useCallback((rawQuery) => {
        const query = rawQuery.trim().toLowerCase();
        if (!query) return;

        setIsRefining(true);
        setTimeout(() => {
            setIsRefining(false);
            setAiRefinementQuery("");

            let msg = "1 node updated";
            if (query.includes("add")) msg = "2 nodes added";
            else if (query.includes("rebuild") || query.includes("replace")) msg = "Workflow rebuilt";

            dispatch(showAlert({ message: msg, type: "success" }));

            if (workflowNodes.length > 0) {
                setNodes((nds) => {
                    const start = getWorkflowStartNode(nds);
                    const updated = getWorkflowNodes(nds).map((n, i) => (
                        i < (query.includes("add") ? 2 : 1)
                            ? { ...n, data: { ...n.data, selected: true } }
                            : n
                    ));
                    return start ? [start, ...updated] : withWorkflowStartNode(updated);
                });
                setTimeout(() => {
                    setNodes((nds) => nds.map((n) => (
                        isWorkflowStartCanvasNode(n)
                            ? n
                            : { ...n, data: { ...n.data, selected: false } }
                    )));
                }, 1000);
            }
        }, 2500);
    }, [dispatch, setNodes, workflowNodes.length]);

    const handleRefine = () => {
        if (!aiRefinementQuery.trim()) return;

        const query = aiRefinementQuery.toLowerCase();

        if (query.includes("rebuild") || query.includes("replace")) {
            setReplaceWorkflowConfirmOpen(true);
            return;
        }

        executeRefine(aiRefinementQuery);
    };

    const handleConfirmReplaceWorkflow = () => {
        setReplaceWorkflowConfirmOpen(false);
        executeRefine(aiRefinementQuery);
    };

    if (workflowDetailLoading) {
        return <BuilderSkeleton />;
    }

    if (workflowDetailError) {
        return (
            <div className="flex-1 flex flex-col items-center justify-center h-full min-h-[300px] p-6 text-center">
                <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
                <p className="text-red-700 dark:text-red-300 font-semibold mb-4">{workflowDetailError}</p>
                <Button onClick={() => navigate("/whatsapp/workflows")}>
                    Back to Workflows
                </Button>
            </div>
        );
    }

    return (
        <div className={`flex flex-col flex-1 min-h-0 min-w-0 overflow-hidden -m-3 animate-fade-in ${workflowTheme.pageFill}`}>
            {/* Top Bar */}
            <header className={`h-14 border-b ${workflowTheme.border} ${workflowTheme.header} flex items-center justify-between gap-2 px-4 shrink-0 z-10 min-w-0 overflow-hidden`}>
                <div className="flex items-center gap-2 sm:gap-4 min-w-0 shrink">
                    <Button
                        variant="ghost"
                        size="icon"
                        className={workflowTheme.backIconButton}
                        onClick={() => confirmLeave(() => navigate(
                            isNewWorkflowRoute(id) ? "/whatsapp/workflows/create" : "/whatsapp/workflows"
                        ))}
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </Button>
                    <div className="flex items-center gap-2 min-w-0">
                        <input
                            className={`font-bold text-lg ${workflowTheme.textPrimary} border-none outline-none focus:ring-2 focus:ring-[#0F6E56] dark:focus:ring-emerald-500 rounded px-1 -ml-1 bg-transparent hover:bg-gray-50 dark:hover:bg-slate-800 min-w-0 max-w-[12rem] sm:max-w-[16rem] lg:max-w-none`}
                            value={name}
                            onChange={e => setName(e.target.value)}
                        />
                        <span className={`${isLive ? workflowTheme.badgeLive : workflowTheme.badgeDraft} text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider`}>
                            {isLive ? "LIVE" : "DRAFT"}
                        </span>
                    </div>
                </div>

                <div className="hidden flex-1 max-w-xl mx-4 min-w-0">
                    <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Sparkles className={`w-4 h-4 ${isRefining ? 'text-[#0F6E56] animate-pulse' : 'text-gray-400 group-focus-within:text-[#0F6E56]'} transition-colors`} />
                        </div>
                        <input
                            type="text"
                            className={`w-full ${workflowTheme.input} rounded-full py-1.5 pl-9 pr-24 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F6E56]/20 dark:focus:ring-emerald-500/20 focus:border-[#0F6E56] dark:focus:border-emerald-500 transition-all`}
                            placeholder="Describe what to change..."
                            value={aiRefinementQuery}
                            onChange={e => setAiRefinementQuery(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleRefine()}
                            disabled={isRefining}
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center">
                            <Button
                                size="sm"
                                variant="ghost"
                                className={`h-full rounded-r-full px-4 text-xs font-semibold ${aiRefinementQuery.trim() ? 'text-[#0F6E56] dark:text-emerald-400 hover:bg-[#E1F5EE] dark:hover:bg-emerald-950/50' : 'text-gray-400 dark:text-gray-500'}`}
                                onClick={handleRefine}
                                disabled={!aiRefinementQuery.trim() || isRefining}
                            >
                                {isRefining ? <Loader2 className="w-3 h-3 animate-spin" /> : "Regenerate"}
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-0.5 mr-1">
                        <Button
                            variant="ghost"
                            size="icon"
                            className={`h-8 w-8 ${canUndo ? "text-gray-700 dark:text-gray-200" : "text-gray-300 dark:text-gray-600"}`}
                            onClick={handleUndo}
                            disabled={!canUndo}
                            title="Undo (Ctrl+Z)"
                            aria-label="Undo"
                        >
                            <Undo2 className="w-4 h-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className={`h-8 w-8 ${canRedo ? "text-gray-700 dark:text-gray-200" : "text-gray-300 dark:text-gray-600"}`}
                            onClick={handleRedo}
                            disabled={!canRedo}
                            title="Redo (Ctrl+Y)"
                            aria-label="Redo"
                        >
                            <Redo2 className="w-4 h-4" />
                        </Button>
                    </div>
                    <Button
                        variant="outline"
                        className="text-[#0F6E56] dark:text-emerald-400 border-[#9FE1CB] dark:border-emerald-800 hover:bg-[#E1F5EE] dark:hover:bg-emerald-950/50"
                        onClick={handlePlayDemo}
                    >
                        <CirclePlay className="w-4 h-4 mr-2" /> Play Demo
                    </Button>
                    <Button
                        variant="ghost"
                        className="text-gray-600 dark:text-gray-300"
                        onClick={handleSave}
                        disabled={isAnyActionPending}
                    >
                        {isSaving || isMediaUploading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <Save className="w-4 h-4 mr-2" />
                        )}
                        Save Draft
                    </Button>
                    <Button
                        className="bg-[hsl(158,48%,36%)] hover:bg-[hsl(158,48%,30%)] text-white font-semibold"
                        onClick={handlePublish}
                        disabled={isAnyActionPending}
                    >
                        {isPublishing ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <Rocket className="w-4 h-4 mr-2" />
                        )}
                        Publish
                    </Button>
                </div>
            </header>

            {/* Validation Errors Banner */}
            {validationErrors.length > 0 && (
                <div className={`${workflowTheme.errorBanner} border-b p-3 flex gap-3 px-6 z-20 shadow-sm`}>
                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                    <div>
                        <h4 className={`text-sm font-bold ${workflowTheme.errorTitle}`}>{validationErrors.length} issue(s) to fix before publishing:</h4>
                        <ul className={`list-disc pl-5 mt-1 text-xs ${workflowTheme.errorText} space-y-0.5`}>
                            {validationErrors.map((err, i) => <li key={i}>{err}</li>)}
                        </ul>
                    </div>
                </div>
            )}

            <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                {/* Tabs Bar */}
                <div className={`flex px-4 border-b ${workflowTheme.tabBar} shrink-0 min-w-0 overflow-x-auto`}>
                    <button
                        className={`px-4 py-2.5 text-sm font-medium border-b-2 flex items-center gap-2 ${activeTab === 'builder' ? workflowTheme.tabActive : workflowTheme.tabInactive}`}
                        onClick={() => setActiveTab('builder')}
                    >
                        <GitBranch className="w-4 h-4" /> Builder
                    </button>
                    <button
                        className={`!hidden px-4 py-2.5 text-sm font-medium border-b-2 items-center gap-2 ${activeTab === 'knowledge' ? workflowTheme.tabActive : workflowTheme.tabInactive}`}
                        onClick={() => setActiveTab('knowledge')}
                    >
                        <Database className="w-4 h-4" /> Knowledge Base
                    </button>
                    <button
                        className={`!hidden px-4 py-2.5 text-sm font-medium border-b-2 items-center gap-2 ${activeTab === 'orchestrator' ? workflowTheme.tabActive : workflowTheme.tabInactive}`}
                        onClick={() => setActiveTab('orchestrator')}
                    >
                        <Bot className="w-4 h-4" /> AI Orchestrator
                    </button>
                    <button
                        className={`px-4 py-2.5 text-sm font-medium border-b-2 flex items-center gap-2 ${activeTab === 'settings' ? workflowTheme.tabActive : workflowTheme.tabInactive}`}
                        onClick={() => setActiveTab('settings')}
                    >
                        <Settings className="w-4 h-4" /> Workflow Settings
                    </button>
                </div>

                <div className="flex-1 flex min-w-0 overflow-hidden relative">
                    {(activeTab === 'builder' || activeTab === 'settings') && (
                        <>
                            <div
                                className={`shrink-0 overflow-hidden transition-[width] duration-200 ease-in-out ${
                                    paletteOpen ? "w-64" : "w-0 pointer-events-none"
                                }`}
                                aria-hidden={!paletteOpen}
                            >
                                <LeftPalette onClose={() => setPaletteOpen(false)} />
                            </div>

                            {!paletteOpen && (
                                <PaletteDrawerHandle onOpen={() => setPaletteOpen(true)} />
                            )}

                            <div className="flex-1 relative min-w-0 h-full overflow-hidden" ref={reactFlowWrapper}>
                                {id === 'new-flow-ai' && workflowNodes.length > 0 && (
                                    <div className={`absolute top-4 left-1/2 -translate-x-1/2 z-10 ${workflowTheme.aiBanner} px-4 py-2 rounded-full shadow-sm text-sm font-medium flex items-center gap-2 animate-in slide-in-from-top-4`}>
                                        <Sparkles className="w-4 h-4 text-[#0F6E56] dark:text-emerald-400" />
                                        AI built this workflow from your description. Click any node to review and edit it before publishing.
                                    </div>
                                )}
                                <ReactFlow
                                    nodes={nodes}
                                    edges={canvasEdges}
                                    onNodesChange={activeTab === 'settings' ? undefined : handleNodesChange}
                                    onEdgesChange={activeTab === 'settings' ? undefined : handleEdgesChange}
                                    onNodeDragStart={activeTab === 'settings' ? undefined : onNodeDragStart}
                                    onConnect={activeTab === 'settings' ? undefined : onConnect}
                                    onDrop={activeTab === 'settings' ? undefined : onDrop}
                                    onDragOver={activeTab === 'settings' ? undefined : onDragOver}
                                    onNodeClick={activeTab === 'settings' ? undefined : onNodeClick}
                                    onEdgeClick={activeTab === 'settings' ? undefined : onEdgeClick}
                                    onPaneClick={activeTab === 'settings' ? undefined : onPaneClick}
                                    nodesDraggable={activeTab !== 'settings'}
                                    nodesConnectable={activeTab !== 'settings'}
                                    elementsSelectable={activeTab !== 'settings'}
                                    nodeTypes={nodeTypes}
                                    edgeTypes={edgeTypes}
                                    defaultEdgeOptions={WORKFLOW_EDGE_OPTIONS}
                                    proOptions={{ hideAttribution: true }}
                                    className={WORKFLOW_CANVAS_CLASS}
                                >
                                    <WorkflowCanvasViewport
                                        ready={hydrationReady}
                                        nodeCount={Math.max(workflowNodes.length, 1)}
                                        canvasKey={id}
                                    />
                                    <Background variant={BackgroundVariant.Dots} gap={24} size={WORKFLOW_CANVAS_DOT_SIZE} color={canvasDotColor} />
                                    <Controls className={workflowTheme.controls} />
                                </ReactFlow>
                            </div>

                            {activeTab === 'settings' ? (
                                <WorkflowSettingsPanel
                                    intentName={intentName}
                                    setIntentName={setIntentName}
                                    exampleUtterances={exampleUtterances}
                                    setExampleUtterances={setExampleUtterances}
                                    sessionTimeoutSeconds={sessionTimeoutSeconds}
                                    setSessionTimeoutSeconds={setSessionTimeoutSeconds}
                                />
                            ) : (
                                inspectorOpen && (
                                    <RightInspectorCatalog
                                        selectedNode={selectedNode}
                                        onUpdateNode={updateNodeData}
                                        onDeleteNode={deleteNode}
                                        onClose={() => setInspectorOpen(false)}
                                        onPendingMediaChange={(pending) => {
                                            inspectorPendingMediaRef.current = pending;
                                        }}
                                        onRegisterPendingUploadNode={registerPendingUploadNode}
                                        mediaFlushSignal={mediaFlushSignal}
                                        uploadSilent={uploadSilent}
                                        currentWorkflowId={
                                            isNewWorkflowRoute(id) ? undefined : id
                                        }
                                    />
                                )
                            )}
                        </>
                    )}

                    {activeTab === 'knowledge' && <KnowledgeBaseTab />}
                    {activeTab === 'orchestrator' && <AIOrchestratorTab />}
                </div>
            </div>

            <WorkflowPlayDemoDialog
                open={demoOpen}
                onOpenChange={setDemoOpen}
                workflowName={name}
                script={demoScript}
            />

            <WorkflowUnsavedChangesDialog {...leaveDialogProps} />

            <ConfirmDialog
                open={replaceWorkflowConfirmOpen}
                onOpenChange={setReplaceWorkflowConfirmOpen}
                title="Replace workflow"
                description="This will replace the current workflow. Continue?"
                confirmLabel="Continue"
                tone="warning"
                confirmVariant="primary"
                onCancel={() => setReplaceWorkflowConfirmOpen(false)}
                onConfirm={handleConfirmReplaceWorkflow}
            />
        </div>
    );
}

export default function BuilderPage() {
    const params = useParams();
    const routeId = params?.id || "new";

    return (
        <ReactFlowProvider>
            <WorkFlowBuilder id={routeId} />
        </ReactFlowProvider>
    );
}
